import React, { useState } from 'react';
import { Table, Tooltip, Transfer } from 'antd';
import type {
  ColumnsType,
  Key,
  TablePaginationConfig,
  TableRowSelection,
} from 'antd/es/table/interface';
import type { InternshipProcessWithNames } from '../Types/interfaces';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';
import { FilterFilled, LockOutlined } from '@ant-design/icons';
import { useInternshipProcessesOfUniversitySupervisor } from '../hooks/useInternshipProcesses';
import '../Assets/Styles/TableTransfer.css';

const EMPTY_SUPERVISOR_PROCESSES: InternshipProcessWithNames[] = [];

export interface TableTransferDataType {
  key: string;
  disabled?: boolean;
  process: {
    id: number;
    student: {
      id: number;
      name: string;
    };
  };
  studyLine: {
    id: number;
    name: string;
  };
  tutor: {
    id: number;
    name: string;
    organization: string;
  };
}

export function InternshipProcessWithNamesToTableTransferDataType(
  process: InternshipProcessWithNames
): TableTransferDataType {
  return {
    key: process.id.toString(),
    process: {
      id: process.id,
      student: {
        id: process.studentId,
        name: process.studentName,
      },
    },
    studyLine: {
      id: 0,
      name: process.studyLineName,
    },
    tutor: {
      id: process.tutorId,
      name: process.tutorName,
      organization: process.organizationName,
    },
  };
}

interface TableTransferProps {
  processes: InternshipProcessWithNames[];
  setTargets: React.Dispatch<React.SetStateAction<TableTransferDataType[]>>;
  selectedSupervisorId: number | undefined;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
}

const TableTransfer = ({
  processes,
  setTargets,
  selectedSupervisorId,
}: TableTransferProps): JSX.Element => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  const { data: supervisorProcesses = EMPTY_SUPERVISOR_PROCESSES, isLoading } =
    useInternshipProcessesOfUniversitySupervisor(selectedSupervisorId ?? 0);
  const [userAddedKeys, setUserAddedKeys] = useState<string[]>([]);
  const { t } = useTranslation();
  const disabledKeys = supervisorProcesses.map((p) => p.id.toString());
  const apiTargetKeys = supervisorProcesses
    .filter((p) => p.universitySupervisorId === selectedSupervisorId)
    .map((p) => p.id.toString());

  const onChange = (nextTargetKeys: Key[]): void => {
    setUserAddedKeys(nextTargetKeys as string[]);
    const targets = dataSource.filter(
      (e) => nextTargetKeys.includes(e.key) && !disabledKeys.includes(e.key)
    );
    setTargets(targets);
  };

  const onSelectChange = (
    sourceSelectedKeys: Key[],
    targetSelectedKeys: Key[]
  ): void => {
    setSelectedKeys([
      ...(sourceSelectedKeys as string[]),
      ...(targetSelectedKeys as string[]),
    ]);
  };

  const targetKeys = Array.from(new Set([...apiTargetKeys, ...userAddedKeys]));
  const combinedProcesses = [...processes];

  // if it doesn't contain combinedProcess then add it
  supervisorProcesses.forEach((supProc) => {
    if (!combinedProcesses.some((p) => p.id === supProc.id)) {
      combinedProcesses.push(supProc);
    }
  });

  const dataSource = combinedProcesses.map((p) => {
    const data = InternshipProcessWithNamesToTableTransferDataType(p);
    return {
      ...data,
      disabled: disabledKeys.includes(data.key),
    };
  });

  return (
    <Transfer
      listStyle={{
        width: '100%',
        minHeight: '40vh',
      }}
      dataSource={dataSource}
      className="responsive-mobile-transfer"
      showSearch={true}
      targetKeys={targetKeys}
      selectedKeys={selectedKeys}
      onChange={onChange}
      onSelectChange={onSelectChange}
      filterOption={(inputValue, item) =>
        item.studyLine.name.indexOf(inputValue) !== -1 ||
        item.process.student.name.indexOf(inputValue) !== -1 ||
        item.tutor.name.indexOf(inputValue) !== -1 ||
        item.tutor.organization.indexOf(inputValue) !== -1
      }
    >
      {({
        filteredItems,
        onItemSelectAll,
        onItemSelect,
        selectedKeys: listSelectedKeys,
      }) => {
        const filtersForStudyLine = Array.from(
          new Set(filteredItems.map((p) => p.studyLine.name))
        )
          .filter((studyLineName) => studyLineName)
          .map((studyLineName) => ({
            text: studyLineName,
            value: studyLineName,
          }));

        const filtersForTutor = Array.from(
          new Set(filteredItems.map((p) => p.tutor.name))
        )
          .filter((tutorName) => tutorName)
          .map((tutorName) => ({
            text: tutorName,
            value: tutorName,
          }));

        const tableColumns: ColumnsType<TableTransferDataType> = [
          {
            dataIndex: 'studyLine',
            title: t('tableTransfer.studyline'),
            render: (sl) => sl.name,
            filters: filtersForStudyLine,
            onFilter: (value, record) => record.studyLine.name === value,
            filterIcon: (filtered: boolean) => (
              <FilterFilled
                style={{
                  fontSize: '20px',
                  color: filtered ? '#1677ff' : undefined,
                }}
              />
            ),
            filterSearch: true,
          },
          {
            dataIndex: 'process',
            title: t('tableTransfer.name'),
            render: (p) => p.student.name,
          },
          {
            dataIndex: 'tutor',
            title: t('tableTransfer.tutor'),
            render: (tutor) => `${tutor.name} (${tutor.organization})`,
            filters: filtersForTutor,
            onFilter: (value, record) => record.tutor.name === value,
            filterIcon: (filtered: boolean) => (
              <FilterFilled
                style={{
                  fontSize: '20px',
                  color: filtered ? '#1677ff' : undefined,
                }}
              />
            ),
            filterSearch: true,
          },
        ];

        const rowSelection: TableRowSelection<TableTransferDataType> = {
          getCheckboxProps: (item) => {
            return {
              disabled: item.disabled,
              style: item.disabled ? { visibility: 'hidden' } : undefined,
            };
          },
          renderCell: (_checked, record, _index, originNode) => {
            const isDisabled = disabledKeys.includes(record.key as string);

            if (isDisabled) {
              return (
                <Tooltip title={t('tableTransfer.alreadyAssigned')}>
                  <LockOutlined
                    style={{ color: '#214175', cursor: 'not-allowed' }}
                  />
                </Tooltip>
              );
            }
            return originNode;
          },
          onSelectAll(_selected, selectedRows) {
            const selectedKeys = selectedRows.map(({ key }) => key);
            onItemSelectAll(selectedKeys, 'replace');
          },
          onSelect({ key }, selected) {
            onItemSelect(key, selected);
          },
          selectedRowKeys: listSelectedKeys,
        };

        return (
          <Table
            size="small"
            loading={isLoading}
            rowSelection={rowSelection}
            columns={tableColumns}
            dataSource={filteredItems}
            scroll={{ x: 'max-content' }}
            onRow={({ key }) => ({
              onClick: (): void => {
                if (disabledKeys.includes(key)) return;
                onItemSelect(key, !listSelectedKeys.includes(key));
              },
            })}
            pagination={{
              pageSize: tableParams.pagination?.pageSize,
              showSizeChanger: true,
              showLessItems: true,
              pageSizeOptions: [10, 20, 30, 50],
              onChange: (page, pageSize) => {
                setTableParams({
                  pagination: {
                    current: page,
                    pageSize: pageSize,
                  },
                });
              },
            }}
          />
        );
      }}
    </Transfer>
  );
};

export default TableTransfer;
